import { supabase } from '@/lib/supabase';

/**
 * Coupons / promotions (0079).
 * Admin-only: pre-Stripe these are applied via the cockpit. When Stripe
 * lands the same rows get stripe_coupon_id / stripe_promotion_code_id
 * populated and customer-facing flows go through Stripe.
 */

export interface Coupon {
  id: string;
  code: string;
  name: string;
  discount_type: 'percent' | 'amount_cad';
  discount_value: number;
  duration: 'once' | 'repeating' | 'forever';
  duration_in_months: number | null;
  max_redemptions: number | null;
  times_redeemed: number;
  redeem_by: string | null;
  active: boolean;
  stripe_coupon_id: string | null;
  stripe_promotion_code_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export async function listCoupons(): Promise<Coupon[]> {
  const { data, error } = await supabase.rpc('admin_list_coupons');
  if (error) throw error;
  return (data ?? []) as Coupon[];
}

export async function createCoupon(input: {
  code: string;
  name: string;
  discountType: 'percent' | 'amount_cad';
  discountValue: number;
  duration: 'once' | 'repeating' | 'forever';
  durationInMonths: number | null;
  maxRedemptions: number | null;
  redeemByIso: string | null;
}): Promise<Coupon> {
  const { data, error } = await supabase.rpc('admin_create_coupon', {
    p_code: input.code,
    p_name: input.name,
    p_discount_type: input.discountType,
    p_discount_value: input.discountValue,
    p_duration: input.duration,
    p_duration_in_months: input.durationInMonths,
    p_max_redemptions: input.maxRedemptions,
    p_redeem_by: input.redeemByIso,
  });
  if (error) throw error;
  return data as Coupon;
}

export async function deactivateCoupon(couponId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('admin_deactivate_coupon', {
    p_coupon_id: couponId,
    p_reason: reason,
  });
  if (error) throw error;
}

export async function applyDiscount(input: {
  orgId: string;
  discountPct: number | null;
  discountAmountOffCad: number | null;
  discountEndsAtIso: string | null;
  reason: string;
  couponId: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('admin_apply_discount', {
    p_org_id: input.orgId,
    p_discount_pct: input.discountPct,
    p_discount_amount_off_cad: input.discountAmountOffCad,
    p_discount_ends_at: input.discountEndsAtIso,
    p_discount_reason: input.reason,
    p_coupon_id: input.couponId,
  });
  if (error) throw error;
}

export async function removeDiscount(orgId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc('admin_remove_discount', {
    p_org_id: orgId,
    p_reason: reason,
  });
  if (error) throw error;
}

export function describeCoupon(c: Coupon): string {
  const head = c.discount_type === 'percent'
    ? `${c.discount_value}% off`
    : `$${c.discount_value} CAD off`;
  const dur =
    c.duration === 'once' ? 'one-time'
    : c.duration === 'forever' ? 'forever'
    : `for ${c.duration_in_months} months`;
  return `${head}, ${dur}`;
}
